import { useMemo } from "react";
import { useProfileDraft } from "@/context/ProfileDraftContext";
import { Button } from "@/components/ui/button";
import { RotateCcw, Save, Palette, Type, Sparkles, PanelBottom, Sliders } from "lucide-react";
import type { HeroOverrides } from "@profolio/templates/types";

import { GlobalStyleSubView } from "./subviews/GlobalStyleSubView";
import { HeroSubView } from "./subviews/HeroSubView";
import { EffectsSubView } from "./subviews/EffectsSubView";
import { FooterSubView } from "./subviews/FooterSubView";
import { AdvancedSubView } from "./subviews/AdvancedSubView";

export function DesignStudio() {
  const {
    draft,
    selectedNode,
    setSelectedNode,
    designPreviewOverride,
    setDesignPreviewOverride,
    discardDesignChanges,
    hasTransientDesignChanges,
    applyDesignChanges,
    userTier,
    onUpgradeClick,
  } = useProfileDraft();

  const isPro = userTier === "pro" || userTier === "premium";
  const profile = draft?.profile;

  // Derive effective local design from committed profile + transient overrides
  const currentDesign = useMemo(() => {
    if (!profile) return null;
    return {
      ...profile,
      ...(designPreviewOverride || {}),
      layoutOverrides: {
        ...(profile.layoutOverrides || {}),
        ...(designPreviewOverride?.layoutOverrides || {}),
        sections: {
          ...(profile.layoutOverrides?.sections || {}),
          ...(designPreviewOverride?.layoutOverrides?.sections || {}),
        },
        sectionOverridesById: {
          ...(profile.layoutOverrides?.sectionOverridesById || {}),
          ...(designPreviewOverride?.layoutOverrides?.sectionOverridesById || {}),
        },
        hero: {
          ...(profile.layoutOverrides?.hero || {}),
          ...(designPreviewOverride?.layoutOverrides?.hero || {}),
        },
        effects: {
          ...((profile.layoutOverrides as any)?.effects || {}),
          ...((designPreviewOverride?.layoutOverrides as any)?.effects || {}),
        },
      },
      customColors:
        designPreviewOverride?.customColors !== undefined
          ? designPreviewOverride.customColors
          : profile.customColors,
    };
  }, [profile, designPreviewOverride]);

  if (!currentDesign) {
    return (
      <div className="flex h-64 items-center justify-center text-xs text-muted-foreground">
        Loading appearance workspace...
      </div>
    );
  }

  const updateTransientPreview = (updates: Partial<typeof currentDesign>) => {
    const nextOverride = {
      ...(designPreviewOverride || {}),
      ...updates,
      layoutOverrides: {
        ...(currentDesign.layoutOverrides || {}),
        ...(updates.layoutOverrides || {}),
        sections: {
          ...(currentDesign.layoutOverrides?.sections || {}),
          ...(updates.layoutOverrides?.sections || {}),
        },
        sectionOverridesById: {
          ...(currentDesign.layoutOverrides?.sectionOverridesById || {}),
          ...(updates.layoutOverrides?.sectionOverridesById || {}),
        },
        hero: {
          ...(currentDesign.layoutOverrides?.hero || {}),
          ...(updates.layoutOverrides?.hero || {}),
        },
        effects: {
          ...((currentDesign.layoutOverrides as any)?.effects || {}),
          ...((updates.layoutOverrides as any)?.effects || {}),
        },
      },
    };
    setDesignPreviewOverride(nextOverride);
  };

  const handleUpdateHeroField = <K extends keyof HeroOverrides>(key: K, value: HeroOverrides[K]) => {
    const currentHero = currentDesign.layoutOverrides?.hero || {};
    const currentLayout = currentDesign.layoutOverrides || {};
    updateTransientPreview({
      layoutOverrides: {
        ...currentLayout,
        hero: {
          ...currentHero,
          [key]: value,
        },
      },
    });
  };

  let title = "Design Studio";
  if (selectedNode?.kind === "siteStyle") title = "Site Style";
  if (selectedNode?.kind === "header") title = "Header & Hero";
  if (selectedNode?.kind === "effects") title = "Atmosphere & Visual Effects";
  if (selectedNode?.kind === "footer") title = "Footer & Branding";
  if (selectedNode?.kind === "advanced") title = "Advanced Customization";

  return (
    <div className="flex flex-col h-full bg-background min-w-0">
      {/* Sticky Canvas Header */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-md border-b border-border/70 shrink-0">
        <div className="h-14 px-4 sm:px-7 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="text-base sm:text-lg font-bold tracking-tight text-foreground truncate">
              {title}
            </h2>
            {hasTransientDesignChanges && (
              <span className="hidden sm:inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400 border border-amber-500/20">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
                Unsaved
              </span>
            )}
          </div>

          {hasTransientDesignChanges && (
            <div className="flex items-center gap-2 shrink-0 animate-in fade-in duration-150">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={discardDesignChanges}
                className="h-8 text-xs px-2.5 cursor-pointer"
              >
                <RotateCcw className="h-3 w-3 mr-1" />
                Discard
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={applyDesignChanges}
                className="h-8 text-xs bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-3 cursor-pointer shadow-xs gap-1.5"
              >
                <Save className="h-3.5 w-3.5" />
                <span>Apply Changes</span>
              </Button>
            </div>
          )}
        </div>

        {/* Global Design Sub-Navigation Tabs */}
        <div className="px-4 sm:px-7 pb-2.5 flex items-center gap-1 overflow-x-auto select-none">
          <div className="inline-flex items-center p-1 rounded-xl bg-muted/60 gap-1 overflow-x-auto max-w-full select-none" role="tablist">
            <button
              type="button"
              onClick={() => setSelectedNode({ kind: "siteStyle" })}
              aria-selected={selectedNode?.kind === "siteStyle"}
              role="tab"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer shrink-0 select-none ${
                selectedNode?.kind === "siteStyle"
                  ? "bg-primary text-primary-foreground shadow-xs font-bold"
                  : "text-muted-foreground hover:text-foreground hover:bg-background/40 font-medium"
              }`}
            >
              <Palette className="h-3.5 w-3.5" />
              <span>Site Style</span>
            </button>

            <button
              type="button"
              onClick={() => setSelectedNode({ kind: "header" })}
              aria-selected={selectedNode?.kind === "header"}
              role="tab"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer shrink-0 select-none ${
                selectedNode?.kind === "header"
                  ? "bg-primary text-primary-foreground shadow-xs font-bold"
                  : "text-muted-foreground hover:text-foreground hover:bg-background/40 font-medium"
              }`}
            >
              <Type className="h-3.5 w-3.5" />
              <span>Header</span>
            </button>

            <button
              type="button"
              onClick={() => setSelectedNode({ kind: "effects" })}
              aria-selected={selectedNode?.kind === "effects"}
              role="tab"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer shrink-0 select-none ${
                selectedNode?.kind === "effects"
                  ? "bg-primary text-primary-foreground shadow-xs font-bold"
                  : "text-muted-foreground hover:text-foreground hover:bg-background/40 font-medium"
              }`}
            >
              <Sparkles className="h-3.5 w-3.5" />
              <span>Effects & Edge Curve</span>
            </button>

            <button
              type="button"
              onClick={() => setSelectedNode({ kind: "footer" })}
              aria-selected={selectedNode?.kind === "footer"}
              role="tab"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer shrink-0 select-none ${
                selectedNode?.kind === "footer"
                  ? "bg-primary text-primary-foreground shadow-xs font-bold"
                  : "text-muted-foreground hover:text-foreground hover:bg-background/40 font-medium"
              }`}
            >
              <PanelBottom className="h-3.5 w-3.5" />
              <span>Footer</span>
            </button>

            <button
              type="button"
              onClick={() => setSelectedNode({ kind: "advanced" })}
              aria-selected={selectedNode?.kind === "advanced"}
              role="tab"
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer shrink-0 select-none ${
                selectedNode?.kind === "advanced"
                  ? "bg-primary text-primary-foreground shadow-xs font-bold"
                  : "text-muted-foreground hover:text-foreground hover:bg-background/40 font-medium"
              }`}
            >
              <Sliders className="h-3.5 w-3.5" />
              <span>Advanced</span>
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8 max-w-4xl w-full mx-auto space-y-12">
        {selectedNode?.kind === "siteStyle" && (
          <GlobalStyleSubView
            currentDesign={currentDesign}
            updateTransientPreview={updateTransientPreview}
            isPro={isPro}
            onUpgradeClick={onUpgradeClick}
            onNavigateToEffects={() => setSelectedNode({ kind: "effects" })}
          />
        )}

        {selectedNode?.kind === "header" && (
          <HeroSubView
            hero={currentDesign.layoutOverrides?.hero || {}}
            userTier={userTier}
            currentThemeKey={currentDesign.themeKey}
            styleRecipe={currentDesign.styleRecipe}
            currentPhotoUrl={currentDesign.photoUrl}
            fullName={currentDesign.fullName}
            onPhotoUpdate={(newPhotoUrl) => {
              updateTransientPreview({ photoUrl: newPhotoUrl });
            }}
            onFullNameUpdate={(newFullName) => {
              updateTransientPreview({ fullName: newFullName });
            }}
            onUpdate={handleUpdateHeroField}
            onUpgradeClick={() => onUpgradeClick("layout_overrides")}
          />
        )}

        {selectedNode?.kind === "effects" && (
          <EffectsSubView
            currentDesign={currentDesign}
            updateTransientPreview={updateTransientPreview}
            isPro={isPro}
            onUpgradeClick={onUpgradeClick}
          />
        )}

        {selectedNode?.kind === "footer" && (
          <FooterSubView
            currentDesign={currentDesign}
            updateTransientPreview={updateTransientPreview}
            isPro={isPro}
            onUpgradeClick={onUpgradeClick}
          />
        )}

        {selectedNode?.kind === "advanced" && (
          <AdvancedSubView
            updateTransientPreview={updateTransientPreview}
          />
        )}
      </div>
    </div>
  );
}
